
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Search, Users, Globe } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getServerDetailPath } from '@/utils/i18nRouting';
import { useSteamServerStatus } from '@/hooks/useSteamServerStatus';
import DOMPurify from 'dompurify';
import { generateSEOAltText } from '@/utils/seoAltTextGenerator';
import { OptimizedImage } from '@/components/OptimizedImage';

const ServerItem = ({ server }: { server: any }) => {
  const { t, language } = useLanguage();
  const isSteamGame = server.games?.is_steam === true;
  
  // Utiliser l'API Steam pour les jeux Steam ET avec une adresse IP
  const { data: steamData } = useSteamServerStatus(
    server.id, 
    isSteamGame && server.ip_address && server.ip_address !== '0.0.0.0'
  );
  
  // Utiliser les données Steam si disponible, sinon les données de la DB
  const displayPlayers = isSteamGame && steamData ? steamData.players : server.online_players;
  const displayMaxPlayers = isSteamGame && steamData ? steamData.maxPlayers : server.max_players;
  const displayOnline = isSteamGame && steamData ? steamData.online : server.is_online;

  return (
    <>
      {server.games.logo_url ? (
        <OptimizedImage
          src={server.games.logo_url}
          alt={generateSEOAltText({
            type: 'game-logo',
            name: server.games.name,
            language
          })}
          imageType="logo"
          width={40}
          height={40}
          loading="lazy"
          className="w-10 h-10 rounded-lg object-cover"
          fallback={
            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-slate-400" />
            </div>
          }
        />
      ) : (
        <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
          <Globe className="w-5 h-5 text-slate-400" />
        </div>
      )}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-white truncate">{server.name}</h4>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className={`w-2 h-2 rounded-full ${displayOnline ? 'bg-green-400' : 'bg-red-400'}`} />
            <span>{displayOnline ? t('server.online') : t('server.offline')}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-1">
          <span className="text-sm text-slate-400">{server.games.name}</span>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {/* Afficher les stats joueurs seulement pour Steam avec IP valide */}
            {isSteamGame && server.ip_address && server.ip_address !== '0.0.0.0' && (
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                <span>{displayPlayers || 0}/{displayMaxPlayers || 100}</span>
              </div>
            )}
            <span>{server.total_votes || 0} {t('search.votes')}</span>
          </div>
        </div>
        
        {server.description && (
          <p 
            className="text-xs text-slate-500 mt-1 truncate"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                server.description
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em>$1</em>'),
                { ALLOWED_TAGS: ['strong', 'em'], ALLOWED_ATTR: [] }
              )
            }}
          />
        )}
      </div>
    </>
  );
};

export const SearchDropdown = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) return [];
      
      const { data, error } = await supabase
        .from('servers_public')
        .select(`
          id,
          slug,
          name,
          description,
          ip_address,
          total_votes,
          online_players,
          max_players,
          is_online,
          games (name, logo_url, is_steam)
        `)
        .eq('is_approved', true)
        .eq('is_hidden', false) // Exclure les serveurs masqués
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      if (error) throw error;
      return data || [];
    },
    enabled: searchQuery.length >= 2,
  });

  useEffect(() => {
    setIsOpen(searchQuery.length >= 2);
  }, [searchQuery]);

  const handleServerClick = (server: { id: string; slug?: string | null }) => {
    const to = getServerDetailPath(language, server.slug ? { slug: server.slug } : { id: server.id });
    navigate(to);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <label htmlFor="server-search" className="sr-only">
          {language === 'fr' ? 'Rechercher un serveur' : language === 'es' ? 'Buscar servidor' : 'Search server'}
        </label>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" aria-hidden="true" />
        <Input 
          id="server-search"
          type="search"
          placeholder={t('search.placeholder')}
          className="pl-10 retro-input w-full"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchQuery.length >= 2 && setIsOpen(true)}
          aria-label={language === 'fr' ? 'Rechercher un serveur' : language === 'es' ? 'Buscar servidor' : 'Search server'}
          role="searchbox"
          aria-controls="search-results"
          aria-expanded={isOpen}
        />
      </div>

      {isOpen && searchQuery.length >= 2 && (
        <>
          {/* Overlay pour fermer */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div 
            id="search-results"
            role="listbox"
            aria-label={language === 'fr' ? 'Résultats de recherche' : language === 'es' ? 'Resultados de búsqueda' : 'Search results'}
            className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto"
          >
            {isLoading ? (
              <div className="p-4 text-center text-slate-400">
                {t('common.loading')}
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <div className="py-2">
                {searchResults.map((server) => (
                  <div
                    key={server.id}
                    className="px-4 py-3 hover:bg-slate-800 cursor-pointer border-b border-slate-700 last:border-b-0"
                    onClick={() => handleServerClick(server)}
                  >
                    <div className="flex items-center gap-3">
                      <ServerItem server={server} />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchQuery.length >= 2 ? (
              <div className="p-4 text-center text-slate-400">
                {t('search.noResults').replace('{query}', searchQuery)}
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};
